import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import { useQuickplayStart } from '../api/quickplayApi';
import { buildOAuthCallbackUrl } from '../components/routing/loginRedirect';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabaseClient';
import { QuickplayShell } from '../components/quickplay/QuickplayShell';

// Virtual build steps shown while the real work runs: step 0 covers the
// sign-in phase, steps 1-3 cycle on a timer during squad generation, and the
// last step lands when the server confirms the team. Layout follows the
// "Building your squad" page in new-ui-kit-designs/nova-playable-demo.html.
const BUILD_STEPS = [
    'Scouting 2,500 real players…',
    'Signing your goalkeepers…',
    'Balancing the budget…',
    'Printing the shirts…',
    'Your team is ready!'
];
const BUILD_FLAVOR_INTERVAL_MS = 900;
const QUICKPLAY_ACTIVE_STATUSES = new Set([
    'waitingForReady',
    'waitingForEngine',
    'inProgress'
]);

function isAnonymousSignInDisabledError(error) {
    const code = String(error?.code || '').toLowerCase();
    const message = String(error?.message || '').toLowerCase();

    return (
        code === 'anonymous_provider_disabled' ||
        message.includes('anonymous sign-ins are disabled') ||
        message.includes('anonymous provider is disabled')
    );
}

function QuickPlayPage() {
    const navigate = useNavigate();
    const { session, user, loading: authLoading, ensureUserProfile, loginWithOAuth } = useAuth();
    const quickplayStart = useQuickplayStart();
    const [stageIndex, setStageIndex] = useState(0);
    const [flavorIndex, setFlavorIndex] = useState(0);
    const [error, setError] = useState('');
    const [showGoogleFallback, setShowGoogleFallback] = useState(false);
    const startedRef = useRef(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const beginQuickplay = useCallback(async () => {
        setError('');
        setShowGoogleFallback(false);
        setStageIndex(0);

        try {
            if (!session) {
                const { error: anonymousError } = await supabase.auth.signInAnonymously();
                if (anonymousError) {
                    throw anonymousError;
                }

                await ensureUserProfile();
            } else if (!user) {
                await ensureUserProfile();
            }

            if (!mountedRef.current) return;

            setStageIndex(1);
            posthog.capture('quickplay_started');
            const result = await quickplayStart.mutateAsync({ deferKickoff: true });

            if (!result?.leagueSlug || !result?.matchId || !result?.teamId) {
                throw new Error('Nova prepared Quick Play but did not return its destination.');
            }

            if (!mountedRef.current) return;

            setStageIndex(2);

            await new Promise((resolve) => window.setTimeout(resolve, 200));
            if (mountedRef.current) {
                const isActiveMatch = QUICKPLAY_ACTIVE_STATUSES.has(result.status);
                const destination = isActiveMatch
                    ? `/${result.leagueSlug}/match/${result.matchId}/match`
                    : `/${result.leagueSlug}/team-pitch/${result.teamId}`;
                navigate(destination, {
                    replace: true,
                    // The team pitch learns the league gameMode and team
                    // ownership from requests that land after its own data,
                    // so hand it what this flow already knows. Without the
                    // hint the squad reveal cannot show on the first render.
                    state: isActiveMatch
                        ? undefined
                        : { squadReveal: { teamId: result.teamId, gameMode: 'quickplay' } }
                });
            }
        } catch (startError) {
            if (!mountedRef.current) return;

            if (isAnonymousSignInDisabledError(startError)) {
                setShowGoogleFallback(true);
                setError('Guest play is temporarily unavailable. Continue with Google to start just as quickly.');
                return;
            }

            setError(
                startError?.response?.data?.error ||
                startError?.message ||
                'Nova could not start Quick Play. Please try again.'
            );
        }
    }, [ensureUserProfile, navigate, quickplayStart, session, user]);

    useEffect(() => {
        if (authLoading || startedRef.current) return;
        startedRef.current = true;
        beginQuickplay();
    }, [authLoading, beginQuickplay]);

    // While the squad is generating, walk through the flavor steps so the
    // page reads like a build in progress rather than a frozen line.
    useEffect(() => {
        setFlavorIndex(0);
        if (stageIndex !== 1) return undefined;

        const interval = window.setInterval(() => {
            setFlavorIndex((current) => {
                if (current >= 2) {
                    window.clearInterval(interval);
                    return current;
                }
                return current + 1;
            });
        }, BUILD_FLAVOR_INTERVAL_MS);
        return () => window.clearInterval(interval);
    }, [stageIndex]);

    const handleGoogleFallback = async () => {
        setError('');
        try {
            await loginWithOAuth('google', {
                redirectTo: buildOAuthCallbackUrl('/play')
            });
        } catch (oauthError) {
            setError(oauthError?.message || 'Google sign-in failed. Please try again.');
        }
    };

    const handleRetry = () => {
        startedRef.current = true;
        beginQuickplay();
    };

    const currentStep = stageIndex === 0
        ? 0
        : stageIndex === 1
            ? 1 + flavorIndex
            : BUILD_STEPS.length - 1;
    const progressPercent = Math.round(((currentStep + 1) / BUILD_STEPS.length) * 100);

    return (
        <QuickplayShell>
            <main className="play-onboarding">
                <section className="squad-builder" aria-labelledby="quickplay-title">
                    <p className="play-kicker">Quick Play</p>
                    <h1 id="quickplay-title" className="squad-builder__title">Building your squad</h1>

                    {!error ? (
                        <>
                            <p className="squad-builder__step" aria-live="polite">
                                {BUILD_STEPS[currentStep]}
                            </p>
                            <div
                                className="squad-builder__track"
                                role="progressbar"
                                aria-label="Quick Play progress"
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-valuenow={progressPercent}
                            >
                                <i
                                    className="squad-builder__fill"
                                    style={{ width: `${progressPercent}%` }}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="play-error" role="alert">
                            <p>{error}</p>
                            {showGoogleFallback ? (
                                <button
                                    type="button"
                                    className="play-choice-button primary"
                                    onClick={handleGoogleFallback}
                                >
                                    Continue with Google
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="play-choice-button primary"
                                    onClick={handleRetry}
                                >
                                    Try again
                                </button>
                            )}
                        </div>
                    )}
                </section>
            </main>
        </QuickplayShell>
    );
}

export { QuickPlayPage };
